import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Bell, AlertTriangle, TrendingUp, TrendingDown, Package, Trash2, DollarSign, CheckCircle, Search, RefreshCw, Loader2, Sparkles, X } from 'lucide-react';

const CATEGORY_OPTS = [
  { value: 'all', label: 'Todas las categorias' },
  { value: 'precio', label: 'Cambios de Precio' },
  { value: 'stock', label: 'Stock Bajo' },
  { value: 'merma', label: 'Mermas Altas' },
  { value: 'food_cost', label: 'Food Cost Alto' },
];

const SEVERITY_OPTS = [
  { value: 'all', label: 'Todas las severidades' },
  { value: 'critical', label: 'Criticas' },
  { value: 'warning', label: 'Advertencias' },
  { value: 'info', label: 'Informativas' },
];

export default function Alerts() {
  const { axiosAuth, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [showResolved, setShowResolved] = useState(false);
  const [search, setSearch] = useState('');
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const currency = user?.currency || 'USD';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (severity !== 'all') params.append('severity', severity);
      if (showResolved) params.append('resolved', 'true');
      const res = await axiosAuth().get(`/alerts?${params.toString()}`);
      setAlerts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, severity, showResolved]);

  const resolveAlert = async (alertId) => {
    try {
      await axiosAuth().put(`/alerts/${alertId}/resolve`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { console.error(e); }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const res = await axiosAuth().post('/alerts/ai-analyze');
      setAiInsights(res.data);
    } catch (e) { alert('Error en analisis AI'); }
    finally { setAiLoading(false); }
  };

  const getIcon = (alert) => {
    switch (alert.type) {
      case 'price_increase': return <TrendingUp size={18} className="text-red-400" />;
      case 'price_drop': return <TrendingDown size={18} className="text-emerald-400" />;
      case 'low_stock': return <Package size={18} className="text-amber-400" />;
      case 'high_waste': return <Trash2 size={18} className="text-orange-400" />;
      case 'cost_anomaly': return <DollarSign size={18} className="text-red-400" />;
      default: return <Bell size={18} className="text-white/40" />;
    }
  };

  const getSeverityColor = (s) => {
    switch (s) {
      case 'critical': return 'border-red-500/30 bg-red-500/5';
      case 'warning': return 'border-amber-500/30 bg-amber-500/5';
      default: return 'border-blue-500/30 bg-blue-500/5';
    }
  };

  const getSeverityBadge = (s) => {
    switch (s) {
      case 'critical': return 'bg-red-500/20 text-red-400';
      case 'warning': return 'bg-amber-500/20 text-amber-400';
      default: return 'bg-blue-500/20 text-blue-400';
    }
  };

  const filtered = search
    ? alerts.filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.message.toLowerCase().includes(search.toLowerCase()))
    : alerts;

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Alertas Inteligentes</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Centro de Alertas</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} disabled={loading} data-testid="refresh-alerts-btn"
            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white/60 text-xs hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button onClick={runAiAnalysis} disabled={aiLoading} data-testid="ai-analyze-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Analisis AI
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="alerts-summary">
        <div className="p-5 bg-[#0A0A0A] border border-white/5">
          <p className="text-2xl font-bold">{alerts.length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Total Alertas</p>
        </div>
        <div className="p-5 bg-[#0A0A0A] border border-red-500/10">
          <p className="text-2xl font-bold text-red-400">{criticalCount}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Criticas</p>
        </div>
        <div className="p-5 bg-[#0A0A0A] border border-amber-500/10">
          <p className="text-2xl font-bold text-amber-400">{warningCount}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Advertencias</p>
        </div>
        <div className="p-5 bg-[#0A0A0A] border border-blue-500/10">
          <p className="text-2xl font-bold text-blue-400">{alerts.length - criticalCount - warningCount}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Info</p>
        </div>
      </div>

      {/* AI Insights */}
      {aiInsights && (
        <div className="bg-gold/5 border border-gold/20 p-6" data-testid="ai-insights">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-gold" />
              <p className="text-gold text-sm font-semibold">Analisis AI</p>
            </div>
            <button onClick={() => setAiInsights(null)} className="text-white/30 hover:text-white"><X size={16} /></button>
          </div>
          {aiInsights.alerts?.map((insight, i) => (
            <div key={i} className="mb-3 p-3 bg-white/5 border border-white/10">
              <p className="text-sm font-medium mb-1">{insight.title}</p>
              <p className="text-xs text-white/60">{insight.description}</p>
              {insight.recommendation && <p className="text-xs text-gold mt-1">{insight.recommendation}</p>}
            </div>
          ))}
          {aiInsights.summary && <p className="text-xs text-white/40 mt-4 italic">{aiInsights.summary}</p>}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3" data-testid="alerts-filters">
        <div className="w-48">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Categoria</label>
          <MobileDropdown options={CATEGORY_OPTS} value={category} onChange={setCategory} testId="alert-category-filter" />
        </div>
        <div className="w-48">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Severidad</label>
          <MobileDropdown options={SEVERITY_OPTS} value={severity} onChange={setSeverity} testId="alert-severity-filter" />
        </div>
        <label className="flex items-center gap-2 cursor-pointer py-3">
          <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} className="w-4 h-4 accent-gold" />
          <span className="text-xs text-white/40">Mostrar resueltas</span>
        </label>
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-3.5 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar alerta..." data-testid="alert-search"
            className="w-full bg-[#0A0A0A] border border-white/10 py-3 pl-9 pr-4 text-sm focus:border-gold focus:outline-none" />
        </div>
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 size={24} className="animate-spin text-gold" /></div>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {filtered.map((alert, i) => (
            <div key={alert.id} className={`border p-5 transition-all ${getSeverityColor(alert.severity)}`} data-testid={`alert-item-${i}`}>
              <div className="flex items-start gap-4">
                <div className="mt-0.5">{getIcon(alert)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-sm font-semibold">{alert.title}</h4>
                    <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest ${getSeverityBadge(alert.severity)}`}>
                      {alert.severity === 'critical' ? 'Critica' : alert.severity === 'warning' ? 'Advertencia' : 'Info'}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 bg-white/5 text-white/30 uppercase tracking-widest">
                      {alert.category === 'precio' ? 'Precio' : alert.category === 'stock' ? 'Stock' : alert.category === 'merma' ? 'Merma' : 'Food Cost'}
                    </span>
                  </div>
                  <p className="text-xs text-white/60 mb-2">{alert.message}</p>
                  {alert.action && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-[10px] text-gold uppercase tracking-widest">Accion:</span>
                      <span className="text-xs text-white/50">{alert.action}</span>
                    </div>
                  )}
                  <p className="text-[10px] text-white/20">{alert.created_at?.slice(0, 16)?.replace('T', ' ')}</p>
                </div>
                <button onClick={() => resolveAlert(alert.id)} data-testid={`resolve-alert-${i}`}
                  className="flex-shrink-0 p-2 text-white/20 hover:text-emerald-400 transition-colors" title="Marcar como resuelta">
                  <CheckCircle size={18} />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <CheckCircle size={40} className="mx-auto text-emerald-400/30 mb-4" />
              <p className="text-white/30 text-sm">No hay alertas activas</p>
              <p className="text-white/15 text-xs mt-1">Todo esta bajo control</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
